import {
  createShaderProgram,
  createUnitQuad,
  requireAttribute,
  requireBuffer,
  UniformLocations,
} from "./shader-program.js";
import type { ClipTransform } from "./layout.js";
import type { XrayDots } from "./xray-dots.js";

/* The X-ray's dots, in one color, drawn into the face's own context before the face's dots,
   so the face covers them until a wave scatters it. One instanced draw of a unit quad, as
   the face draws. The strength fades the whole X-ray in and out, and at 0 nothing draws. */

// A dot smaller than this many device pixels doesn't draw: it would only flicker.
const MIN_DEVICE_RADIUS = 0.15;

const VERTEX_SHADER_SRC = `#version 300 es
in vec2 aCorner;   // unit quad corner in [-1,1], per vertex
in vec2 aCenter;   // dot center in artwork units [0,1024], per instance
in float aRadius;  // dot radius in artwork units, per instance
uniform vec4 uTransform; // (sx, sy, ox, oy): clipX = ax*sx+ox, clipY = ay*sy+oy
uniform float uPixel;    // one device pixel, in clip units
out vec2 vCorner;
out float vEdge;   // where the dot's outline is, in the quad's corner units
void main() {
  float rClip = aRadius * abs(uTransform.x);
  if (rClip < ${MIN_DEVICE_RADIUS.toFixed(2)} * uPixel) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);  // off-screen, so clipped away
    return;
  }
  // The quad grows by a device pixel, so the smooth edge has room outside the outline.
  float quad = rClip + uPixel;
  vCorner = aCorner;
  vEdge = rClip / quad;
  vec2 c = vec2(aCenter.x * uTransform.x + uTransform.z, aCenter.y * uTransform.y + uTransform.w);
  gl_Position = vec4(c + aCorner * quad, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SRC = `#version 300 es
precision highp float;
in vec2 vCorner;
in float vEdge;
uniform vec3 uColor;     // sRGB, 0 to 1
uniform float uStrength; // 0 to 1
out vec4 fragColor;
void main() {
  // 1 on the outline, as on the face's dots, with the same one-device-pixel fade across it.
  float edge = length(vCorner) / vEdge;
  float aa = 0.5 * fwidth(edge);
  float coverage = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, edge);
  if (coverage <= 0.0) discard;
  fragColor = vec4(uColor, 1.0) * coverage * uStrength;
}
`;

const XRAY_UNIFORMS = ["uTransform", "uPixel", "uColor", "uStrength"] as const;

/** The X-ray's dots for the face's context. The face calls `draw()` after it clears. */
export class XrayLayer {
  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly quadBuffer: WebGLBuffer;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly u: UniformLocations<(typeof XRAY_UNIFORMS)[number]>;
  private readonly count: number;
  private level = 0;

  static async create(gl: WebGL2RenderingContext, dots: XrayDots): Promise<XrayLayer> {
    const program = await createShaderProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC);
    try {
      return new XrayLayer(gl, dots, program);
    } catch (error) {
      gl.deleteProgram(program);
      throw error;
    }
  }

  private constructor(gl: WebGL2RenderingContext, dots: XrayDots, program: WebGLProgram) {
    this.gl = gl;
    const cleanup: Array<() => void> = [];
    try {
      const u = new UniformLocations(gl, program, XRAY_UNIFORMS, "XrayLayer");
      const aCorner = requireAttribute(gl, program, "aCorner", "XrayLayer");
      const aCenter = requireAttribute(gl, program, "aCenter", "XrayLayer");
      const aRadius = requireAttribute(gl, program, "aRadius", "XrayLayer");

      const vao = gl.createVertexArray();
      if (!vao) throw new Error("XrayLayer: gl.createVertexArray returned null");
      cleanup.push(() => gl.deleteVertexArray(vao));
      gl.bindVertexArray(vao);

      const quadBuffer = createUnitQuad(gl, aCorner, "XrayLayer");
      cleanup.push(() => gl.deleteBuffer(quadBuffer));

      const floats = 3;
      const instanceData = new Float32Array(dots.count * floats);
      for (let i = 0; i < dots.count; i++) {
        instanceData[i * floats] = dots.x[i]!;
        instanceData[i * floats + 1] = dots.y[i]!;
        instanceData[i * floats + 2] = dots.r[i]!;
      }
      const instanceBuffer = requireBuffer(gl, "XrayLayer instance");
      cleanup.push(() => gl.deleteBuffer(instanceBuffer));
      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, instanceData, gl.STATIC_DRAW);

      const stride = floats * Float32Array.BYTES_PER_ELEMENT;
      gl.enableVertexAttribArray(aCenter);
      gl.vertexAttribPointer(aCenter, 2, gl.FLOAT, false, stride, 0);
      gl.vertexAttribDivisor(aCenter, 1);
      gl.enableVertexAttribArray(aRadius);
      gl.vertexAttribPointer(
        aRadius,
        1,
        gl.FLOAT,
        false,
        stride,
        2 * Float32Array.BYTES_PER_ELEMENT,
      );
      gl.vertexAttribDivisor(aRadius, 1);

      gl.bindVertexArray(null);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);

      this.program = program;
      this.u = u;
      this.vao = vao;
      this.quadBuffer = quadBuffer;
      this.instanceBuffer = instanceBuffer;
      this.count = dots.count;
    } catch (error) {
      for (const release of cleanup.reverse()) release();
      throw error;
    }
  }

  setTransform(t: ClipTransform): void {
    this.gl.useProgram(this.program);
    this.gl.uniform4f(this.u.get("uTransform"), t.sx, t.sy, t.ox, t.oy);
  }

  /** The canvas's width in device pixels, for the smallest dot and the smooth edge. */
  resize(deviceW: number): void {
    this.gl.useProgram(this.program);
    this.gl.uniform1f(this.u.get("uPixel"), 2 / Math.max(deviceW, 1));
  }

  setColor(rgb: readonly [number, number, number]): void {
    this.gl.useProgram(this.program);
    this.gl.uniform3f(this.u.get("uColor"), rgb[0], rgb[1], rgb[2]);
  }

  setStrength(strength: number): void {
    this.level = Math.min(1, Math.max(0, strength));
    this.gl.useProgram(this.program);
    this.gl.uniform1f(this.u.get("uStrength"), this.level);
  }

  get strength(): number {
    return this.level;
  }

  /** Draws the dots over whatever the framebuffer holds. It leaves its own program bound and
      the blend on, so the face binds its program again after. */
  draw(): void {
    if (this.level <= 0 || this.count === 0) return;
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.count);
    gl.bindVertexArray(null);
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteBuffer(this.quadBuffer);
    gl.deleteBuffer(this.instanceBuffer);
    gl.deleteVertexArray(this.vao);
  }
}
